import { useContextControl } from '@onepercentio/one-ui/context/ContextAsyncControl'
import useUniqueEffect from '@onepercentio/one-ui/hooks/utility/useUniqueEffect'
import { Context, useContext, useMemo, useState } from 'react'

export type QueriableContextShape<Entity> = {
  entities: Entity[] | undefined
  id: string
}

export function useQueriableContext<E, F>(
  ctxI: QueriableContextShape<E>,
  entityValues: F[] | undefined,
  fieldToMatchWith: (entity: E) => F,
  funcFromCtxThatLoadEntities: (entityIds: F[]) => Promise<void>,
  deps: any[] = []
) {
  const { entities, entitiesToLoad } = useMemo(() => {
    let remainingMatches = entityValues ? [...entityValues] : []
    const resultingAssets = [] as E[]
    if (ctxI.entities)
      for (let entity of ctxI.entities) {
        let matchWith = fieldToMatchWith(entity)
        let found: boolean = remainingMatches.includes(matchWith)
        if (found) {
          resultingAssets.push(entity)
          remainingMatches = remainingMatches.filter((a) => a !== matchWith)
        }
      }

    return {
      entities: resultingAssets,
      entitiesToLoad: remainingMatches as F[],
    }
  }, [ctxI.entities, entityValues, ...deps])

  const allEntitiesLoaded = useMemo(() => {
    if (entityValues === undefined) return true
    return entities.length === entityValues.length
  }, [entities, entityValues])

  const ctrlId = `${ctxI.id}-load-${entitiesToLoad?.join('-')}`

  const control = useContextControl(ctrlId, {
    loadEntities: (ids: F[]) => funcFromCtxThatLoadEntities(ids),
  })
  useUniqueEffect(
    ctrlId,
    () => {
      if (entitiesToLoad.length && !control.loading)
        control.loadEntities(entitiesToLoad)
    },
    [entitiesToLoad.length]
  )

  return {
    entities,
    allEntitiesLoaded,
    loading: control.loading,
    error: control.error,
    retry: () => control.loadEntities(entitiesToLoad),
  }
}
